import http from "./http-common";
import Crypto from "./Crypto";
import { getServidor, getServidorPorAmbiente } from "../utils/Utils";
import JWT from "./Jwt";


/**
 * @class AlertasDataService
 * @brief Servicio que proporciona métodos para interactuar con los endpoints
 * relacionados con las alertas.
 */
class AlertasDataService{
    /**
     * @brief Método que retorna el listado de alertas.
     * @returns {Promise<AxiosResponse<any>>} Respuesta con el listado de alertas
     */
    getAll() {
      return http.get(`${getServidor()}/alertas/`, {
        showLoader: false
      });
    }
    
    /**
     * @brief Fetches all alerts for the current month from the server.
     * @returns {Promise<AxiosResponse<any>>} Respuesta con las alertas del mes.
     */
    getAllPorMes() {
      return http.get(`${getServidor()}/alertas/allAlertaPorMes`, {
        showLoader: false
      });
    }

    /**
     * @brief Fetches all alerts for the current month from the server.
     * @param data Datos utilizados para realizar la consulta.
     * @param key Clave utilizada para encriptar los datos.
     * @returns {Promise<AxiosResponse<any>>} Respuesta con las notificaciones.
     */
    async getPorMesNotificacionesPorPartes(data,key) {
      const datosEncriptados = await Crypto.encriptar(data, key);
      return http.get(`${getServidor()}/alertas/getPorMesNotificacionesPorPartes`, {
        params: datosEncriptados,
        showLoader: false
      });
    }

    /**
     * @brief Método que permite actualizar el valor select de la alerta, función que hace posible ver la posición en el mapa
     * @param data variable que almacena los datos de la alerta a actualizar
     * @param key key para encriptar los datos
     * @returns {Promise<AxiosResponse<any>>} Respuesta de la actualización.
    */
    async updateAlerta(data, key) {
      const datosEncriptados = await Crypto.encriptar(data, key);
      return http.put(`${getServidor()}/alertas/updateSelect`, datosEncriptados);
    }

    /**
     * @brief Método que permite actualizar el valor select de true a false en todas las alertas
     * @returns {Promise<AxiosResponse<any>>} Respuesta con las alertas del mes.
     */
    updateAlertaFalse(){
      return http.post(`${getServidor()}/alertas/selectFalse`);
    }

    /**
     * @brief Método encargardo de obtener las alertas de un dispositivo mediante un rango de fechas.
     * @param data Datos del dispositivo y rango de fechas.
     * @param key Clave utilizada para encriptar los datos.
     * @returns {Promise<AxiosResponse<any>>} Respuesta del servidor.     
    */
    async getAlertsByDeviceDates(data, key){
      const datosEncriptados = await Crypto.encriptar(data, key);
      return http.get(`${getServidor()}/alertas/obtenerAlertasPorFechasDeDispositivo`, {
        params: datosEncriptados
      });
    }

    /**
      * @brief Método que permite obtener el total de alertas registradas.
      * @return {Promise<AxiosResponse<any>>} Respuesta del servicio a la solicitud para buscar las alertas
      */
    getNumeroAlertas() {
      return http.get(`${getServidor()}/alertas/numeroAlertas`, {
        showLoader: false
      });
    }

    // ==== Servicios para alertas en vista de estadisticas ====
    /**
     * @brief Obtiene las alertas más recientes AGRUPADAS por categoría.
     * @returns {Promise<AxiosResponse<any>>} Promesa con la respuesta del servidor.
     */
    getLatestGroupedAlerts(){
      return http.get(`${getServidor()}/alertas/obtenerUltimasAlertasAgrupadas`, {
        showLoader: false
      });
    }

    /**
      * @brief Obtiene las alertas AGRUPADAS por categoría dentro de un rango de fechas.
      * @param dates Rango de fechas utilizado para realizar la consulta.
      * @param key Clave utilizada para encriptar los datos.
      * @returns {Promise<AxiosResponse<any>>} Promesa con la respuesta del servidor.
      */
    async getAlertsGroupedByDates(dates, key){
      const datosEncriptados = await Crypto.encriptar(dates, key);
      return http.get(`${getServidor()}/alertas/obtenerAlertasAgrupadasPorFechas`, {
        params: datosEncriptados
      });
    }

    /**
      * @brief Obtiene las alertas más recientes por categoría.
      * @param category Categoria de las alertas.
      * @param key Clave utilizada para encriptar los datos.
      * @returns {Promise<AxiosPromise<any>>} Promesa con las alertas.
      */
    async getLatestAlertsByType(category, key){
      const datosEncriptados = await Crypto.encriptar(category, key);
      return http.get(`${getServidor()}/alertas/obtenerUltimasAlertasPorTipo`, {
        params: datosEncriptados
      });
    }

    /**
      * @brief Obtiene las alertas por categoría dentro de un rango de fechas.
      * @param data Datos de la consulta, incluyendo categoria y fechas.
      * @param key Clave utilizada para encriptar los datos.
      * @returns {Promise<AxiosPromise<any>>} Respuesta con las alertas.
      */
    async getAlertsByDatesAndType(data, key){
      const datosEncriptados = await Crypto.encriptar(data, key);
      return http.get(`${getServidor()}/alertas/obtenerAlertasPorFechasYTipo`, {
        params: datosEncriptados
      });
    }

    /**
      * @brief Obtiene las alertas de tipo Geocerca más recientes AGRUPADAS por categoría (Entrada/Salida).
      * @returns {Promise<AxiosResponse<any>>} Respuesta con las geocercas agrupadas.
      */
    getLatestGroupedGeofences(){
      return http.get(`${getServidor()}/alertas/obtenerUltimasGeocercasAgrupadas`, {
        showLoader: false
      });
    }

    /**
      * @brief Obtiene las alertas de tipo Geocerca AGRUPADAS por categoría (Entrada/Salida) dentro de un rango de fechas.
      * @param dates Rango de fechas utilizado para realizar la consulta.
      * @param key Clave utilizada para encriptar los datos.
      * 
      * @returns {Promise<AxiosResponse<any>>} Respuesta con las geocercas agrupadas.
      */
    async getGeofencesGroupedByDates(dates, key){
      const datosEncriptados = await Crypto.encriptar(dates, key);
      return http.get(`${getServidor()}/alertas/obtenerGeocercasAgrupadasPorFechas`, {
        params: datosEncriptados
      });
    }

    /**
      * @brief Obtiene las alertas de tipo Geocerca más recientes por categoría (Entrada/Salida).
      * @param category Categoria de la geocerca.
      * @param key Clave utilizada para encriptar los datos.
      * @returns {Promise<AxiosResponse<any>>} Promesa con la respuesta del servidor con las geocercas.
      */
    async getLatestGeofencesByType(category, key){
      const datosEncriptados = await Crypto.encriptar(category, key);
      return http.get(`${getServidor()}/alertas/obtenerUltimasGeocercasPorTipo`, {
        params: datosEncriptados
      });
    }

    /**
      * @brief Obtiene las alertas de tipo Geocerca por categoría (Entrada/Salida) dentro de un rango de fechas.
      * @param data Datos de la consulta, incluyendo categoria y fechas.
      * @param key Clave utilizada para encriptar los datos.
      * @returns {Promise<AxiosResponse<any>>} Promesa con la respuesta del servidor con las geocercas.
      */
    async getGeofencesByDatesAndType(data, key){
      const datosEncriptados = await Crypto.encriptar(data, key);
      return http.get(`${getServidor()}/alertas/obtenerGeocercasPorFechasYTipo`, {
        params: datosEncriptados
      });
    }

    /**
      * @brief Obtiene las alertas AGRUPADAS por categoría de un dispositivo dentro de un rango de fechas.
      * @param dates Datos del dispositivo y rango de fechas.
      * @param key Clave utilizada para encriptar los datos.
      * @returns {Promise<AxiosResponse<any>>} Respuesta con las aletar agrupadas.
      */
    async getAlertsGroupedByDeviceDates(dates, key){
      const datosEncriptados = await Crypto.encriptar(dates, key);
      return http.get(`${getServidor()}/alertas/obtenerAlertasAgrupadasPorFechasDeDispositivo`, {
        params: datosEncriptados
      });
    }

    /**
      * @brief Obtiene las alertas AGRUPADAS de tipo GEOCERCA por categoría dentro de un rango de fechas de un dispositivo.
      * @param dates Datos del dispositivo y rango de fechas.
      * @param key Clave utilizada para encriptar los datos.
      * @returns {Promise<AxiosResponse<any>>} Respuesta con las geocercas agrupadas.
      */
    async getGeofencesGroupedByDeviceDates(dates, key){
      const datosEncriptados = await Crypto.encriptar(dates, key);
      return http.get(`${getServidor()}/alertas/obtenerGeocercasAgrupadasPorFechasDeDispositivo`, {
        params: datosEncriptados
      });
    }

    /**
      * @brief Obtiene las alertas por categoría dentro de un intervalo de tiempo de un dispositivo.
      * @param data Datos del dispositivo, categoria y rango de fechas.
      * @param key Clave utilizada para encriptar los datos.
      * @returns {Promise<AxiosPromise<any>>} Promesa con la respuesta del servidor.
      */
    async getDeviceAlertsByDateAndType(data, key){
      const datosEncriptados = await Crypto.encriptar(data, key);
      return http.get(`${getServidor()}/alertas/obtenerAlertasDeDispositivoPorFechaYTipo`, {
        params: datosEncriptados
      });
    }

    /**
      * @brief Obtiene las alertas de tipo GEOCERCA por categoría dentro de un intervalo de tiempo de un dispositivo.
      * @param data Datos del dispositivo, categoria y rango de fechas.
      * @param key Clave utilizada para encriptar los datos.
      * @returns {Promise<AxiosResponde<any>>} Promesa con la respuesta del servidor con las geocercas.
    */
    async getDeviceGeofencesByDateAndType(data, key){
      const datosEncriptados = await Crypto.encriptar(data, key);
      return http.get(`${getServidor()}/alertas/obtenerGeocercasDeDispositivoPorFechaYTipo`, {
        params: datosEncriptados
      });
    }

    /**
     * @brief Método que permite obtener alertas filtradas por rango de fechas de todos los dispositivos
     * @param data Datos del dispositivo, categoria y rango de fechas.
     * @param key Clave utilizada para encriptar los datos.
     * @returns {Promise<AxiosResponde<any>>} Promesa con la respuesta del servidor con las geocercas.
    */
    async getAllByDate(data, key){
      const datosEncriptados = await Crypto.encriptar(data, key);
      return http.get(`${getServidor()}/alertas/rangoFecha` , {
        params: datosEncriptados
      });
    }

     /**
       * @brief Método que permite obtener alertas filtradas por rango de fechas  y con paginado de todos los dispositivos
       * @param data Rango de fechas y parametros de consulta.
       * @param key Clave utilizada para encriptar los datos.
       * @return {Promise<AxiosResponde<any>>} Respuesta con las alertas.
     */
     async getAllByDatePaginado(data, key){
      const datosEncriptados = await Crypto.encriptar(data, key);
      return http.get(`${getServidor()}/alertas/rangoFechaPaginado` , {
        params: datosEncriptados
      });
    }

    /**
     * @brief Método que retorna el listado de alertas con paginado.
     * @param data Parametros de consulta y paginacion.
     * @param key Clave utilizada para encriptar los datos.
     * @return {Promise<AxiosResponde<any>>} Respuesta con las alertas paginadas.
     */
    async getAllConPaginado(data, key) {
      const datosEncriptados = await Crypto.encriptar(data, key);
      return http.get(`${getServidor()}/alertas/getAllAlertasConPaginado`, {
        params: datosEncriptados,
        showLoader: false
      });
    }

    /**
     * @brief Obtiene las alartas por dispositivo
     * @returns {Promise<AxiosPromise<any>>} Respuesta con las alertas del dispositivo.
     */
    getAlertasDevice(){
      return http.get(`${getServidorPorAmbiente()}/alertas/compartirLink/`+ JWT.jwtEncode(sessionStorage.id) +
            "?fecha_inicial="+ JWT.jwtEncode(sessionStorage.fechaInicio) +"&fecha_final=" + JWT.jwtEncode(sessionStorage.fechaFin),{
              showLoader: false
            });
    }

    
    
}

/**
 * @brief Instancia del serivicio de alertas.
 * 
 * @type {AlertasDataService} 
 */
export default new AlertasDataService();